import { FC, useCallback, useEffect, useState } from 'react';
import { recentSearches } from '../../../common/constants';
import './SearchBarComponent.scss';

interface SearchBarPropsComponent {
	handleSearch: () => void;
	handleClose?: () => void;
	placeholder?: string;
	startSuggestionSearch: (term: string) => void;
	value?: string;
	reset: () => void;
	handleChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
	handleClearSearch: () => void;
}

export const SearchBarComponent: FC<SearchBarPropsComponent> = ({
	handleSearch,
	placeholder,
	handleClose,
	value,
	reset,
	handleChange,
	handleClearSearch
}) => {
	const [recent, setRecent] = useState<string[]>([]);
	const [showRecent, setShowRecent] = useState(false);
	const defaultPlaceholder = 'Search for products, brands, ingredients and more';

	useEffect(() => {
		const storedRecentSearches = JSON.parse(localStorage.getItem(recentSearches) || '[]');
		setRecent(storedRecentSearches);
	}, []);

	useEffect(() => {
		const loadRecent = () => {
			const stored = JSON.parse(localStorage.getItem(recentSearches) || '[]');
			setRecent(stored);
		};
		loadRecent();
		window.addEventListener('recentSearchUpdated', loadRecent);
		return () => {
			window.removeEventListener('recentSearchUpdated', loadRecent);
		};
	}, []);

	const closeIconCick = useCallback(() => {
		reset();
		handleClose && handleClose();
		handleClearSearch();
	}, [handleClearSearch, handleClose, reset]);
	const handleKeyUp = useCallback(
		(event: React.KeyboardEvent<HTMLInputElement>) => {
			event.preventDefault();
			if (event.key === 'Enter' && event.currentTarget.value.trim()) {
				postSearchItem(event.currentTarget.value.trim());
				handleSearch();
				setShowRecent(false);
			}
		},
		[handleSearch]
	);

	const handleRecentClick = useCallback(
		(term: string) => {
			if (handleChange) {
				handleChange({ target: { value: term } } as React.ChangeEvent<HTMLInputElement>);
			}
			postSearchItem(term);
			setShowRecent(false);
		},
		[handleSearch]
	);

	const postSearchItem = (term: any) => {
		const recentSearchData = [term, ...recent.filter(r => r !== term)].slice(0, 6);
		localStorage.setItem(recentSearches, JSON.stringify(recentSearchData));
		setRecent(recentSearchData);
	};

	const clearRecentSeraches = () => {
		localStorage.removeItem(recentSearches);
		setRecent([]);
	};

	return (
		<div className='search-bar-container'>
			<input
				type='text'
				className={`search-bar ${value ? 'typing' : ''}`}
				value={value}
				onChange={handleChange}
				placeholder={placeholder || defaultPlaceholder}
				onKeyUp={handleKeyUp}
				onFocus={() => {
					setShowRecent(true);
				}}
				onBlur={() => setShowRecent(false)}
			/>
			{showRecent && recent.length > 0 && (
				<div className='recent-dropdown'>
					<div className='recent-header'>
						<span>Recent Searches</span>
					</div>
					<div className='recent-item-list'>
						<ul className='recent-list'>
							{recent.map((r, i) => (
								<li
									key={i}
									className='recent-item'
									onMouseDown={e => {
										e.preventDefault();
										handleRecentClick(r);
									}}
								>
									{r}
								</li>
							))}
						</ul>
					</div>
					<div
						className='clear-recent'
						onMouseDown={e => {
							e.preventDefault();
							clearRecentSeraches();
						}}
					>
						Clear Recent Searches
					</div>
				</div>
			)}
			<div className={`icons-container ${!value ? 'empty-wrapper' : ''}`}>
				{value && <i className='ri-close-line clean-icon' onClick={closeIconCick}></i>}
				<i
					className='ri-search-line search-icon'
					onClick={() => {
						postSearchItem(value);
						handleSearch();
					}}
				></i>
			</div>
		</div>
	);
};
